import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Box, makeStyles, CircularProgress,
  Typography, IconButton, Paper, Divider
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import PersonIcon from '@material-ui/icons/Person';

import MultiLangTextField from '../../components/MultiLangTextField';
import { getProphetById, createProphet, updateProphet } from '../../services/prophetService';
import { storage } from '../../firebase';

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    '& .MuiDialog-paper': {
      margin: 24,
      width: 'calc(100vw - 48px)',
      maxWidth: 'calc(100vw - 48px) !important',
      height: 'calc(100vh - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      borderRadius: 20,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.type === 'dark' ? '#0f172a' : '#ffffff',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 25px 70px rgba(0,0,0,0.35)',
      '@media (max-width: 600px)': {
        margin: 8,
        width: 'calc(100vw - 16px)',
        height: 'calc(100vh - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        borderRadius: 14,
      },
    },
  },
  dialogHeader: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.type === 'dark' ? '#111827' : '#f8fafc',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.2) 100%)',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    borderRadius: 10,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  dialogContent: {
    padding: '24px 28px',
    overflowY: 'auto',
    flexGrow: 1,
    background: theme.palette.type === 'dark' ? '#0b1120' : '#f8fafc',
  },
  dialogActions: {
    padding: '14px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? '#111827' : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
    flexShrink: 0,
  },
  sectionCard: {
    padding: 24,
    borderRadius: 16,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: 20,
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  field: {
    marginBottom: theme.spacing(2.5),
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
      borderRadius: 10,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
    },
  },
  arabicField: {
    '& .MuiInputBase-root': {
      direction: 'rtl',
      fontFamily: '"Traditional Arabic", "Scheherazade New", serif',
      fontSize: '1.45rem',
      lineHeight: 2.0,
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
    },
  },
  uploadBox: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 16,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.3)' : '#f8fafc',
    '&:hover': {
      borderColor: '#10b981',
      background: 'rgba(16,185,129,0.05)',
    },
  },
  previewBox: {
    position: 'relative',
    display: 'inline-block',
    borderRadius: 12,
    overflow: 'hidden',
  },
  previewImg: {
    maxHeight: 180,
    borderRadius: 12,
    display: 'block',
  },
  removeBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    '&:hover': { background: 'rgba(239,68,68,0.8)' },
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#ffffff',
    fontWeight: 600,
    borderRadius: 10,
    padding: '8px 24px',
    boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
    textTransform: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
  },
  cancelBtn: {
    borderRadius: 10,
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 20px',
  },
}));

export default function ProphetFormModal({ open, onClose, prophetId = null, onSuccess }) {
  const classes = useStyles();
  const theme = useTheme();

  const isEdit = Boolean(prophetId);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [spouseTextEn, setSpouseTextEn] = useState('');
  const [spouseTextBn, setSpouseTextBn] = useState('');
  const [sonsTextEn, setSonsTextEn] = useState('');
  const [sonsTextBn, setSonsTextBn] = useState('');

  const [form, setForm] = useState({
    name: { en: '', bn: '' },
    arabicName: '',
    description: { en: '', bn: '' },
    imageUrl: '',
    title: { en: '', bn: '', ar: '' },
    era: { en: '', bn: '', ar: '' },
    nation: { en: '', bn: '', ar: '' },
    family: { spouse: { en: [], bn: [] }, sons: { en: [], bn: [] } },
    miracles: [],
  });

  useEffect(() => {
    if (!open) return;
    setErrorMsg('');
    if (prophetId) {
      setLoading(true);
      getProphetById(prophetId)
        .then((data) => {
          if (data) {
            setForm({
              name: { en: data.name?.en || '', bn: data.name?.bn || '' },
              arabicName: data.arabicName || '',
              description: { en: data.description?.en || '', bn: data.description?.bn || '' },
              imageUrl: data.imageUrl || '',
              title: data.title || { en: '', bn: '', ar: '' },
              era: data.era || { en: '', bn: '', ar: '' },
              nation: data.nation || { en: '', bn: '', ar: '' },
              family: data.family || { spouse: { en: [], bn: [] }, sons: { en: [], bn: [] } },
              miracles: data.miracles || [],
            });

            if (data.family?.spouse) {
              if (Array.isArray(data.family.spouse)) {
                setSpouseTextEn(data.family.spouse.join(', '));
                setSpouseTextBn('');
              } else {
                setSpouseTextEn(data.family.spouse.en?.join(', ') || '');
                setSpouseTextBn(data.family.spouse.bn?.join(', ') || '');
              }
            } else {
              setSpouseTextEn('');
              setSpouseTextBn('');
            }

            if (data.family?.sons) {
              if (Array.isArray(data.family.sons)) {
                setSonsTextEn(data.family.sons.join(', '));
                setSonsTextBn('');
              } else {
                setSonsTextEn(data.family.sons.en?.join(', ') || '');
                setSonsTextBn(data.family.sons.bn?.join(', ') || '');
              }
            } else {
              setSonsTextEn('');
              setSonsTextBn('');
            }
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setErrorMsg('Failed to load Prophet details.');
          setLoading(false);
        });
    } else {
      setForm({
        name: { en: '', bn: '' },
        arabicName: '',
        description: { en: '', bn: '' },
        imageUrl: '',
        title: { en: '', bn: '', ar: '' },
        era: { en: '', bn: '', ar: '' },
        nation: { en: '', bn: '', ar: '' },
        family: { spouse: { en: [], bn: [] }, sons: { en: [], bn: [] } },
        miracles: [],
      });
      setSpouseTextEn('');
      setSpouseTextBn('');
      setSonsTextEn('');
      setSonsTextBn('');
      setLoading(false);
    }
  }, [open, prophetId]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const docId = prophetId || `temp_${Date.now()}`;
      const ref = storage.ref(`prophet_images/${docId}`);
      await ref.put(file, { contentType: file.type || 'image/jpeg' });
      const url = await ref.getDownloadURL();
      setForm((prev) => ({ ...prev, imageUrl: url }));
    } catch (err) {
      console.error(err);
      setErrorMsg('Image upload failed.');
    }
    setUploading(false);
  };

  const handleRemoveImage = () => {
    setForm((prev) => ({ ...prev, imageUrl: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddMiracle = () => {
    setForm((prev) => ({
      ...prev,
      miracles: [...prev.miracles, { en: '', bn: '', ar: '' }],
    }));
  };

  const handleUpdateMiracle = (index, field, value) => {
    setForm((prev) => {
      const updated = [...prev.miracles];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, miracles: updated };
    });
  };

  const handleRemoveMiracle = (index) => {
    setForm((prev) => ({
      ...prev,
      miracles: prev.miracles.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!form.name.en.trim()) {
      setErrorMsg('English name is required.');
      return;
    }
    setSaving(true);
    setErrorMsg('');
    try {
      const saveData = {
        ...form,
        family: {
          spouse: {
            en: spouseTextEn.split(',').map((s) => s.trim()).filter(Boolean),
            bn: spouseTextBn.split(',').map((s) => s.trim()).filter(Boolean),
          },
          sons: {
            en: sonsTextEn.split(',').map((s) => s.trim()).filter(Boolean),
            bn: sonsTextBn.split(',').map((s) => s.trim()).filter(Boolean),
          },
        },
      };

      if (isEdit) {
        await updateProphet(prophetId, saveData);
      } else {
        await createProphet(saveData);
      }

      setSaving(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || 'Failed to save prophet.');
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className={classes.dialogRoot}
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
      scroll="paper"
    >
      <DialogTitle disableTypography className={classes.dialogHeader}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIcon}>
            <PersonIcon />
          </div>
          <div>
            <Typography className={classes.headerTitle}>
              {isEdit ? 'নবী/রাসূল তথ্য সম্পাদনা (Edit Prophet Info)' : 'নতুন নবী/রাসূল যোগ করুন (Add Prophet)'}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {isEdit ? `ID: ${prophetId} • ${form.name.en || 'Prophet'}` : 'আম্বিয়ায়ে কেরামের জীবনী ও সংশ্লিষ্ট তথ্য সংরক্ষণ করুন'}
            </Typography>
          </div>
        </div>

        <IconButton
          onClick={onClose}
          className={classes.closeBtn}
          size="small"
          disabled={saving}
          title="Close Modal"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
            <CircularProgress style={{ color: '#10b981' }} size={48} thickness={4} />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              তথ্য লোড হচ্ছে...
            </Typography>
          </Box>
        ) : (
          <>
            {errorMsg && (
              <Box mb={2.5} p={1.5} borderRadius={8} bgcolor="rgba(239,68,68,0.1)" border="1px solid rgba(239,68,68,0.3)">
                <Typography variant="body2" style={{ color: '#ef4444', fontWeight: 600 }}>
                  <span role="img" aria-label="error">⚠️</span> {errorMsg}
                </Typography>
              </Box>
            )}

            {/* Basic Info */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>মৌলিক পরিচিতি (Basic Information)</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MultiLangTextField
                    fields={form.name}
                    onChange={(name) => setForm({ ...form, name })}
                    label="Prophet Name"
                    title="Name"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="আরবি নাম (Arabic Name)"
                    value={form.arabicName}
                    onChange={(e) => setForm({ ...form, arabicName: e.target.value })}
                    className={`${classes.field} ${classes.arabicField}`}
                    placeholder="مُحَمَّدٌ..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <MultiLangTextField
                    fields={form.description}
                    onChange={(description) => setForm({ ...form, description })}
                    label="Description / সারসংক্ষেপ"
                    title="Description"
                    multiline
                    minRows={2}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Image Upload */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>ছবি / প্রতিকৃতি (Image)</Typography>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleImageUpload}
              />
              {form.imageUrl ? (
                <Box className={classes.previewBox}>
                  <img src={form.imageUrl} alt="Prophet" className={classes.previewImg} />
                  <IconButton className={classes.removeBtn} size="small" onClick={handleRemoveImage}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box className={classes.uploadBox} onClick={() => fileInputRef.current?.click()}>
                  {uploading ? (
                    <CircularProgress size={32} style={{ color: '#10b981' }} />
                  ) : (
                    <>
                      <CloudUploadIcon style={{ fontSize: 40, color: '#10b981', marginBottom: 8 }} />
                      <Typography variant="body2" color="textPrimary" style={{ fontWeight: 600 }}>
                        ছবি আপলোড করতে ক্লিক করুন
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        PNG, JPG, WEBP (Max 5MB)
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Paper>

            {/* Historical Details */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>ঐতিহাসিক প্রেক্ষাপট (Historical Details)</Typography>
              <Box mb={2}>
                <MultiLangTextField
                  fields={form.title}
                  onChange={(val) => setForm({ ...form, title: val })}
                  label="উপাধি / Title (e.g. কালিমুল্লাহ)"
                  title="Title"
                />
              </Box>
              <Box mb={2}>
                <MultiLangTextField
                  fields={form.era}
                  onChange={(val) => setForm({ ...form, era: val })}
                  label="যুগ / Era"
                  title="Era"
                />
              </Box>
              <Box mb={1}>
                <MultiLangTextField
                  fields={form.nation}
                  onChange={(val) => setForm({ ...form, nation: val })}
                  label="জাতি / Nation"
                  title="Nation"
                />
              </Box>
            </Paper>

            {/* Family Info */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>পারিবারিক তথ্য (Family Info - কমা দিয়ে একাধিক নাম দিন)</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Spouse (English, comma separated)"
                    value={spouseTextEn}
                    onChange={(e) => setSpouseTextEn(e.target.value)}
                    className={classes.field}
                    placeholder="e.g. Khadijah, Aisha"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="স্ত্রী (বাংলা, কমা দিয়ে পৃথক)"
                    value={spouseTextBn}
                    onChange={(e) => setSpouseTextBn(e.target.value)}
                    className={classes.field}
                    placeholder="যেমন: খাদিজা (রাঃ), আয়েশা (রাঃ)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Children / Sons (English, comma separated)"
                    value={sonsTextEn}
                    onChange={(e) => setSonsTextEn(e.target.value)}
                    className={classes.field}
                    placeholder="e.g. Qasim, Abdullah, Ibrahim"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="সন্তানাদি (বাংলা, কমা দিয়ে পৃথক)"
                    value={sonsTextBn}
                    onChange={(e) => setSonsTextBn(e.target.value)}
                    className={classes.field}
                    placeholder="যেমন: কাসিম, আবদুল্লাহ, ইব্রাহিম"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Miracles */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                  মু‘জিযাসমূহ (Miracles)
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddMiracle}
                  style={{ textTransform: 'none', borderRadius: 8, borderColor: '#10b981', color: '#10b981' }}
                >
                  Add Miracle
                </Button>
              </Box>

              {form.miracles.map((m, idx) => (
                <Box
                  key={idx}
                  p={2}
                  mb={2}
                  borderRadius={12}
                  border={`1px solid ${theme.palette.divider}`}
                  bgcolor={theme.palette.type === 'dark' ? 'rgba(30,41,59,0.3)' : '#f8fafc'}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      Miracle #{idx + 1}
                    </Typography>
                    <IconButton size="small" onClick={() => handleRemoveMiracle(idx)} style={{ color: '#ef4444' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Description (English)"
                        value={m.en || ''}
                        onChange={(e) => handleUpdateMiracle(idx, 'en', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="বিবরণ (বাংলা)"
                        value={m.bn || ''}
                        onChange={(e) => handleUpdateMiracle(idx, 'bn', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions} style={{ gap: 16 }}>
        <Button onClick={onClose} className={classes.cancelBtn} disabled={saving} style={{ marginRight: 8 }}>
          Cancel
        </Button>

        <Button
          variant="contained"
          className={classes.saveBtn}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? 'সংরক্ষণ হচ্ছে...' : isEdit ? 'আপডেট করুন (Save Changes)' : 'সংরক্ষণ করুন (Save Prophet)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
